'use client';

import {
  CheckIcon,
  CloseIcon,
  ErrorIcon,
  InfoIcon,
  SecondaryButton,
  WarningIcon,
} from '../atomic-atoms';

import styles from './Banner.module.scss';

import type { ReactNode } from 'react';

export type BannerVariant = 'info' | 'success' | 'warning' | 'error';

type BannerProps = {
  title: ReactNode;
  description?: ReactNode;
  variant?: BannerVariant;
  actions?: ReactNode;
  dismissible?: boolean;
  onClose?: () => void;
  closeLabel?: string;
  className?: string;
};

const variantToIcon: Record<BannerVariant, ReactNode> = {
  info: <InfoIcon size={18} />,
  success: <CheckIcon size={18} />,
  warning: <WarningIcon size={18} />,
  error: <ErrorIcon size={18} />,
};

const variantToClassName: Record<BannerVariant, string> = {
  info: styles.info,
  success: styles.success,
  warning: styles.warning,
  error: styles.error,
};

export function Banner({
  title,
  description,
  variant = 'info',
  actions,
  dismissible = false,
  onClose,
  closeLabel = 'Dismiss',
  className,
}: BannerProps) {
  return (
    <div
      role="status"
      className={[styles.banner, variantToClassName[variant], className]
        .filter(Boolean)
        .join(' ')}
    >
      <div className={styles.icon}>{variantToIcon[variant]}</div>

      <div className={styles.content}>
        <div className={styles.title}>{title}</div>
        {description ? (
          <div className={styles.description}>{description}</div>
        ) : null}
        {actions ? <div className={styles.actions}>{actions}</div> : null}
      </div>

      {dismissible ? (
        <div style={{ marginLeft: 'auto' }}>
          <SecondaryButton
            variant="mini"
            ariaLabel={closeLabel}
            onClick={onClose}
            style={{ whiteSpace: 'nowrap' }}
            backgroundOverride="theme"
          >
            <CloseIcon size={16} />
          </SecondaryButton>
        </div>
      ) : null}
    </div>
  );
}
